import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, ArrowRight, Gift, Link as LinkIcon, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { updateCollection } from "@/services/collections";
import { cn } from "@/lib/utils";
import type { Tables } from "@/integrations/supabase/types";

interface GiftGuidanceWizardProps {
  collection: Tables<"collections">;
  onClose: () => void;
  onUpdate: (updates: Partial<Tables<"collections">>) => void;
  embedded?: boolean;
}

function isValidUrl(str: string) {
  try { new URL(str); return true; } catch { return false; }
}

function ListInput({
  items,
  onChange,
  placeholder,
}: {
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");

  const addItem = () => {
    const trimmed = draft.trim();
    if (!trimmed || items.length >= 20) return;
    onChange([...items, trimmed]);
    setDraft("");
  };

  const removeItem = (idx: number) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2">
      {items.length > 0 && (
        <ul className="space-y-1">
          {items.map((item, idx) => (
            <li key={idx} className="flex items-center gap-2 group">
              <span className="w-1 h-1 rounded-full bg-primary/40 shrink-0" />
              <span className="text-sm font-light text-foreground flex-1">{item}</span>
              <button
                type="button"
                onClick={() => removeItem(idx)}
                className="shrink-0 w-6 h-6 rounded flex items-center justify-center text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-destructive transition-all"
              >
                <X size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); addItem(); }
          if (e.key === "Backspace" && !draft && items.length > 0) removeItem(items.length - 1);
        }}
        onBlur={() => { if (draft.trim()) addItem(); }}
        placeholder={placeholder}
        className="border-border text-sm"
      />
      <p className="text-xs text-muted-foreground">
        Press <kbd className="px-1 py-0.5 rounded border border-border text-[10px] font-mono">Enter</kbd> to add
      </p>
    </div>
  );
}

const TOTAL_STEPS = 3;

export default function GiftGuidanceWizard({ collection, onClose, onUpdate, embedded = false }: GiftGuidanceWizardProps) {
  const { toast } = useToast();
  const col = collection as any;

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Step 1: Wants & Has
  const toList = (s: string | null) => (s || "").split(/[\n,]+/).map((v: string) => v.trim()).filter(Boolean);
  const fromList = (items: string[]) => items.join(", ");

  const [wants, setWants] = useState<string[]>(toList(col.preferences_wants));
  const [has, setHas] = useState<string[]>(toList(col.preferences_has));
  const [dislikes, setDislikes] = useState<string[]>(toList(col.preferences_dislikes));

  // Step 3: Style & Registries
  const [style, setStyle] = useState<string[]>(toList(col.preferences_style));
  const [registries, setRegistries] = useState<string[]>(
    col.preferences_registries?.length ? col.preferences_registries : []
  );

  // Step 4: Review & toggle
  const [showInInvite, setShowInInvite] = useState(col.show_preferences_in_invite !== false);

  const handleSave = async () => {
    setSaving(true);
    const validRegistries = registries.filter((r) => r.trim());
    const updates: Record<string, any> = {
      preferences_wants: fromList(wants) || null,
      preferences_has: fromList(has) || null,
      preferences_dislikes: fromList(dislikes) || null,
      preferences_style: fromList(style) || null,
      preferences_registries: validRegistries.length ? validRegistries : [],
      preferences_price_guidance: null,
      show_preferences_in_invite: showInInvite,
    };
    const { error } = await updateCollection(collection.id, updates);
    if (error) {
      toast({ variant: "destructive", title: "Error", description: error.message });
    } else {
      toast({ title: "Saved", description: "Gift guidance updated." });
      onUpdate(updates as any);
      onClose();
    }
    setSaving(false);
  };

  const next = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const addRegistry = () => setRegistries([...registries, ""]);
  const removeRegistry = (idx: number) => setRegistries(registries.filter((_, i) => i !== idx));
  const updateRegistry = (idx: number, val: string) => {
    const copy = [...registries];
    copy[idx] = val;
    setRegistries(copy);
  };

  const recipientName = collection.recipient_name || "them";

  const stepContent = [
    // Step 0: Wants & Has
    <div key="step-0" className="space-y-6">
      <div className="text-center space-y-2">
        <span className="text-3xl">🎁</span>
        <h2 className="text-xl font-bold text-foreground font-serif">What does {recipientName} want?</h2>
        <p className="text-sm text-muted-foreground">
          Help your group choose the perfect gift
        </p>
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-foreground">
          What does {recipientName} want or need?
        </Label>
        <ListInput
          items={wants}
          onChange={setWants}
          placeholder="e.g. UPPAbaby Vista stroller in navy blue"
        />
        <p className="text-xs text-muted-foreground">Specific items, brands, or categories</p>
      </div>
       <div className="space-y-1.5">
        <Label className="text-sm font-medium text-foreground">
          What does {recipientName} already have?
        </Label>
        <ListInput
          items={has}
          onChange={setHas}
          placeholder="e.g. crib, changing table"
        />
        <p className="text-xs text-muted-foreground">Avoid duplicate gifts</p>
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-foreground">
          What does {recipientName} not like?
        </Label>
        <ListInput
          items={dislikes}
          onChange={setDislikes}
          placeholder="e.g. scented candles, dark chocolate"
        />
        <p className="text-xs text-muted-foreground">Things to avoid</p>
      </div>
    </div>,

    // Step 1: Style & Registries
    <div key="step-1" className="space-y-6">
      <div className="text-center space-y-2">
        <span className="text-3xl">✨</span>
        <h2 className="text-xl font-bold text-foreground font-serif">Style & registries</h2>
        <p className="text-sm text-muted-foreground">
          Share {recipientName}'s taste and any wishlists
        </p>
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-foreground">Style or preferences</Label>
        <ListInput
          items={style}
          onChange={setStyle}
          placeholder="e.g. neutral colors, minimalist aesthetic"
        />
        <p className="text-xs text-muted-foreground">Colors, themes, styles they prefer</p>
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-foreground">Registry or wishlist links</Label>
        <div className="space-y-2">
          {registries.map((link, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className="relative flex-1">
                <LinkIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={link}
                  onChange={(e) => updateRegistry(idx, e.target.value)}
                  placeholder="https://..."
                  className={cn("pl-8 border-border", link && !isValidUrl(link) && "border-destructive")}
                />
              </div>
              <button
                onClick={() => removeRegistry(idx)}
                className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
        <Button type="button" variant="ghost" size="sm" className="text-primary text-xs h-8 px-2" onClick={addRegistry}>
          <Plus size={14} className="mr-1" /> Add link
        </Button>
      </div>
    </div>,

    // Step 2: Review
    <div key="step-2" className="space-y-5">
      <div className="text-center space-y-2">
        <span className="text-3xl">👀</span>
        <h2 className="text-xl font-bold text-foreground font-serif">Review & save</h2>
        <p className="text-sm text-muted-foreground">
          Here's what your group will see
        </p>
      </div>

      <div className="bg-muted/30 border border-border rounded-xl p-4 space-y-3 text-sm">
        {(wants.length > 0 || has.length > 0 || dislikes.length > 0 || style.length > 0 || registries.some(r => r.trim() && isValidUrl(r))) && (
          <div className="space-y-2.5">
            <p className="font-semibold text-foreground">Gift Guidance</p>
            {wants.length > 0 && (
              <div>
                <p className="font-medium text-foreground text-xs mb-0.5">What {recipientName} wants</p>
                <p className="text-muted-foreground text-sm">{fromList(wants)}</p>
              </div>
            )}
            {has.length > 0 && (
              <div>
                <p className="font-medium text-foreground text-xs mb-0.5">Already has</p>
                <p className="text-muted-foreground text-sm">{fromList(has)}</p>
              </div>
            )}
            {dislikes.length > 0 && (
              <div>
                <p className="font-medium text-foreground text-xs mb-0.5">Doesn't like</p>
                <p className="text-muted-foreground text-sm">{fromList(dislikes)}</p>
              </div>
            )}
            {style.length > 0 && (
              <div>
                <p className="font-medium text-foreground text-xs mb-0.5">Style preferences</p>
                <p className="text-muted-foreground text-sm">{fromList(style)}</p>
              </div>
            )}
            {registries.some(r => r.trim() && isValidUrl(r)) && (
              <div>
                <p className="font-medium text-foreground text-xs mb-0.5">Registry</p>
                {registries.filter(r => r.trim() && isValidUrl(r)).map((r, i) => (
                  <a key={i} href={r} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm block">
                    {(() => { try { return new URL(r).hostname.replace("www.", ""); } catch { return r; } })()}
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
        {wants.length === 0 && has.length === 0 && dislikes.length === 0 && style.length === 0 && !registries.some(r => r.trim() && isValidUrl(r)) && (
          <p className="text-muted-foreground text-center py-4">No content added yet</p>
        )}
      </div>

      <div className="flex items-start gap-3 pt-2 border-t border-border">
        <Checkbox
          id="showPrefsWizard"
          checked={showInInvite}
          onCheckedChange={(checked) => setShowInInvite(checked === true)}
          className="mt-0.5"
        />
        <div>
          <label htmlFor="showPrefsWizard" className="text-sm font-medium text-foreground cursor-pointer">
            Include gift guidance when inviting contributors
          </label>
          <p className="text-xs text-muted-foreground mt-0.5">
            These preferences will be shared with everyone you invite
          </p>
        </div>
      </div>
    </div>,
  ];

  const progressBar = (
    <div className={embedded ? "pb-2" : "px-6 pt-4"}>
      <div className="flex items-center gap-1.5">
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <div key={i} className={cn("h-1 rounded-full flex-1 transition-colors duration-300", i <= step ? "bg-primary" : "bg-border")} />
        ))}
      </div>
      <p className="text-[10px] text-muted-foreground mt-1.5 text-right">Step {step + 1} of {TOTAL_STEPS}</p>
    </div>
  );

  const body = (
    <div className={embedded ? "py-4" : "flex-1 overflow-y-auto px-6 py-6"}>
      <div className={embedded ? "" : "max-w-lg mx-auto"}>
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
            {stepContent[step]}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );

  const footer = (
    <div className={embedded ? "pt-4 border-t border-border" : "px-6 py-4 border-t border-border"}>
      <div className={cn("flex items-center gap-3", !embedded && "max-w-lg mx-auto")}>
        {step > 0 && (
          <Button variant="outline" onClick={back} className="rounded-full gap-1.5 flex-1">
            <ArrowLeft size={15} /> Back
          </Button>
        )}
        {step < TOTAL_STEPS - 1 ? (
          <Button onClick={next} className="rounded-full gap-1.5 flex-1 bg-primary text-primary-foreground hover:bg-primary/90">
            Next <ArrowRight size={15} />
          </Button>
        ) : (
          <Button onClick={handleSave} disabled={saving} className="rounded-full flex-1 bg-primary text-primary-foreground hover:bg-primary/90">
            {saving ? "Saving..." : "Save"}
          </Button>
        )}
      </div>
    </div>
  );

  if (embedded) {
    return <div className="flex flex-col">{progressBar}{body}{footer}</div>;
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-background flex flex-col">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <button onClick={onClose} className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors">
          <X size={18} />
        </button>
        <div className="flex items-center gap-2">
          <Gift size={16} className="text-primary" />
          <span className="text-sm font-semibold text-foreground">Gift Guidance</span>
        </div>
        <div className="w-9" />
      </div>
      {progressBar}{body}{footer}
    </motion.div>
  );
}
